/**
 * PartialResultRenderer - Reusable component for displaying partial LLM results
 *
 * Renders analytical notes and visualizations incrementally as they arrive from the LLM API.
 * Handles:
 * - Typing animation for analytical notes
 * - Incremental rendering of tables and charts
 * - Tracking rendered content to avoid duplication
 * - Extracting and parsing partial JSON data
 * - Safe DOM operations with Cyrillic text
 */
import React, { useEffect, useRef } from 'react';
import { renderAnalyticalNote, renderVisualizations, executeChartScripts } from '../../../utils/reportRenderer';
import { extractPartialData } from '../../../utils/partialDataExtractor';
import { safeQuerySelector, setSafeElementIdentifiers, createErrorFallbackElement } from '../../../utils/safeDomUtils';
import '../../../styles/reportContent.css';
import '../PartialResult.css';
import '../../../styles/safeDomErrorStyles.css';

/**
 * Interface for block-based content structure
 */
export interface ContentBlock {
  type: 'heading' | 'text' | 'visualization';
  // Heading block
  level?: number;
  text?: string;
  // Text block
  content?: string;
  // Visualization block
  description?: string;
  chart_html?: string;
  table_data?: {
    headers: string[];
    rows: Array<any[] | { values: Array<{ value: string; classes?: string }> }>;
  };
}

/**
 * Interface for partial result data structure
 * Supports both new block-based format and legacy format
 */
export interface PartialResult {
  // New block-based format
  blocks?: ContentBlock[];
  // Legacy format (backward compatibility)
  analytical_note?: string;
  visualizations?: Array<{
    description: string;
    chart_html?: string;
    table_data?: {
      headers: string[];
      rows: Array<any[] | { values: Array<{ value: string; classes?: string }> }>;
    };
  }>;
  partial_text?: string;
}

/**
 * Props for PartialResultRenderer component
 */
interface PartialResultRendererProps {
  /** Partial result data to render */
  result: PartialResult;
  /** Name of the report for context */
  reportName: string;
  /** Optional ref to the container element */
  containerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * PartialResultRenderer Component
 *
 * Displays partial results with incremental rendering and typing animation.
 * Prevents duplicate rendering by tracking what has already been displayed.
 *
 * @example
 * ```tsx
 * const partialResult = {
 *   analytical_note: "Анализ данных показывает...",
 *   visualizations: [{ description: "График продаж", chart_html: "..." }]
 * };
 *
 * <PartialResultRenderer
 *   result={partialResult}
 *   reportName="Отчет о продажах"
 * />
 * ```
 */
export const PartialResultRenderer: React.FC<PartialResultRendererProps> = ({
  result,
  reportName,
  containerRef: externalContainerRef
}) => {
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = externalContainerRef || internalContainerRef;

  // Keep track of what has been rendered to avoid duplication
  const renderedRef = useRef<{
    analyticalNote: string | null;
    visualizations: { [key: string]: boolean };
    blocks: { [key: string]: boolean };
    blockCount: number;
  }>({
    analyticalNote: null,
    visualizations: {},
    blocks: {},
    blockCount: 0
  });

  // Try to parse JSON from partial_text if it exists
  let parsedPartialText: any = null;
  if (result.partial_text) {
    try {
      // First try to parse as complete JSON
      if (result.partial_text.trim().startsWith('{')) {
        try {
          parsedPartialText = JSON.parse(result.partial_text);
          console.log('[BLOCK_RENDERER] Successfully parsed JSON from partial_text:', Object.keys(parsedPartialText));

          // Check if it's the new blocks format
          if (parsedPartialText.blocks) {
            console.log('[BLOCK_RENDERER] Detected new blocks format, count:', parsedPartialText.blocks.length);
          }
        } catch (error) {
          // If complete JSON parsing fails, try to extract partial data
          console.log('[BLOCK_RENDERER] Failed to parse complete JSON, trying to extract partial data');
          parsedPartialText = extractPartialData(result.partial_text);
          if (parsedPartialText && (parsedPartialText.blocks || parsedPartialText.analytical_note ||
            (parsedPartialText.visualizations && parsedPartialText.visualizations.length > 0))) {
            console.log('[BLOCK_RENDERER] Successfully extracted partial data:',
              parsedPartialText.blocks ? `${parsedPartialText.blocks.length} blocks found` : '',
              parsedPartialText.analytical_note ? 'analytical_note found' : '',
              parsedPartialText.visualizations ? `${parsedPartialText.visualizations.length} visualizations found` : '');
          } else {
            console.log('[BLOCK_RENDERER] No usable partial data found');

            // Additional attempt to extract data from incomplete JSON
            try {
              // Try to extract analytical_note directly (legacy format)
              const analyticalNoteRegex = /"analytical_note"\s*:\s*"((?:\\"|[^"])*)(?:"|$)/;
              const analyticalNoteMatch = result.partial_text.match(analyticalNoteRegex);

              if (analyticalNoteMatch && analyticalNoteMatch[1]) {
                console.log('[BLOCK_RENDERER] Found analytical_note directly in partial_text (legacy)');
                parsedPartialText = {
                  analytical_note: analyticalNoteMatch[1]
                    .replace(/\\"/g, '"')
                    .replace(/\\n/g, '<br>')
                    .replace(/\\\\/g, '\\')
                };
              }
            } catch (extractError) {
              console.log('[BLOCK_RENDERER] Error in additional extraction attempt:', extractError);
            }
          }
        }
      }
    } catch (error) {
      console.log('[BLOCK_RENDERER] Failed to process partial_text:', error);
      parsedPartialText = null;
    }
  }

  // Use effect to render the content using reportRenderer
  useEffect(() => {
    if (contentRef.current) {
      // Determine if we're using the new blocks format or legacy format
      const blocks = result.blocks || (parsedPartialText && parsedPartialText.blocks);
      const isBlockFormat = !!blocks;

      console.log('[BLOCK_RENDERER] Rendering with format:', isBlockFormat ? 'blocks' : 'legacy');

      if (isBlockFormat) {
        // NEW BLOCK-BASED RENDERING
        console.log('[BLOCK_RENDERER] Rendering blocks, total:', blocks.length, 'previously rendered:', renderedRef.current.blockCount);

        // Initialize blocks container if needed
        if (!contentRef.current.querySelector('.blocks-container')) {
          const blocksContainer = document.createElement('div');
          blocksContainer.className = 'blocks-container';
          contentRef.current.appendChild(blocksContainer);
        }

        const blocksContainer = contentRef.current.querySelector('.blocks-container') as HTMLElement;

        // Render new blocks that haven't been rendered yet
        for (let i = renderedRef.current.blockCount; i < blocks.length; i++) {
          const block = blocks[i];
          const blockKey = `block-${i}`;

          if (!renderedRef.current.blocks[blockKey]) {
            console.log(`[BLOCK_RENDERER] Rendering block ${i}:`, block.type);

            const blockElement = document.createElement('div');
            blockElement.className = `block block-${block.type}`;
            blockElement.setAttribute('data-block-index', String(i));

            if (block.type === 'heading') {
              const heading = document.createElement(`h${block.level || 2}`) as HTMLHeadingElement;
              heading.textContent = block.text || '';
              blockElement.appendChild(heading);

            } else if (block.type === 'text') {
              const textDiv = document.createElement('div');
              textDiv.className = 'text-content';
              textDiv.innerHTML = block.content || '';
              blockElement.appendChild(textDiv);

            } else if (block.type === 'visualization') {
              const vizContainer = document.createElement('div');
              vizContainer.className = 'visualization-item';

              // Add description as title
              if (block.description) {
                const titleElement = document.createElement('h3');
                titleElement.textContent = block.description;
                vizContainer.appendChild(titleElement);
              }

              // Render table if present
              if (block.table_data) {
                const tableContainer = document.createElement('div');
                tableContainer.className = 'table-container';
                const tableViz = {
                  description: block.description || '',
                  table_data: block.table_data
                };
                renderVisualizations([tableViz], tableContainer);
                vizContainer.appendChild(tableContainer);
              }

              // Render chart if present and complete
              if (block.chart_html && block.chart_html.includes('</script>')) {
                const chartContainer = document.createElement('div');
                chartContainer.className = 'chart-container';
                const chartViz = {
                  description: block.description || '',
                  chart_html: block.chart_html
                };
                renderVisualizations([chartViz], chartContainer);
                vizContainer.appendChild(chartContainer);

                // Defer chart script execution until after the element is added to the DOM
                // (document.getElementById inside the script won't find canvas until it's in the document)
                const deferredChartContainer = chartContainer;
                requestAnimationFrame(() => executeChartScripts(deferredChartContainer));
              }

              blockElement.appendChild(vizContainer);
            }

            blocksContainer.appendChild(blockElement);
            renderedRef.current.blocks[blockKey] = true;
            renderedRef.current.blockCount = i + 1;
          }
        }

      } else {
        // LEGACY FORMAT RENDERING (analytical_note + visualizations)
        console.log('[BLOCK_RENDERER] Using legacy rendering');

        // Initialize content container if it's empty
        if (contentRef.current.children.length === 0) {
          // Create containers for analytical note and visualizations
          const analyticalNoteContainer = document.createElement('div');
          analyticalNoteContainer.className = 'analytical-note-container';
          contentRef.current.appendChild(analyticalNoteContainer);

          const visualizationsContainer = document.createElement('div');
          visualizationsContainer.className = 'visualizations-container';
          contentRef.current.appendChild(visualizationsContainer);
        }

        // Get containers
        const analyticalNoteContainer = contentRef.current.querySelector('.analytical-note-container') as HTMLElement;
        const visualizationsContainer = contentRef.current.querySelector('.visualizations-container') as HTMLElement;

        // Determine what content to render
        const analyticalNote = result.analytical_note ||
          (parsedPartialText && parsedPartialText.analytical_note);

        const visualizations = result.visualizations ||
          (parsedPartialText && parsedPartialText.visualizations) ||
          [];

        // Render analytical note if available and it has changed
        if (analyticalNote && analyticalNote !== renderedRef.current.analyticalNote) {
          // Update the rendered analytical note
          analyticalNoteContainer.innerHTML = '';
          renderAnalyticalNote(analyticalNote, analyticalNoteContainer);
          renderedRef.current.analyticalNote = analyticalNote;
        }

        // Render new visualizations if available
        if (visualizations && visualizations.length > 0) {
          console.log('[PARTIAL_RESULT] Processing visualizations:', visualizations.length);

          // Define the visualization type
          type Visualization = {
            description: string;
            chart_html?: string;
            table_data?: {
              headers: string[];
              rows: Array<any[] | { values: Array<{ value: string; classes?: string }> }>;
            };
          };

          // Process each visualization separately
          visualizations.forEach((viz: Visualization, vizIndex: number) => {
            console.log(`[PARTIAL_RESULT] Processing visualization #${vizIndex}: "${viz.description || 'unnamed'}"`,
              'has table:', !!viz.table_data,
              'has chart:', !!viz.chart_html);

            // Create unique keys for table and chart components
            const tableKey = viz.description + "-table" + (viz.table_data ? JSON.stringify(viz.table_data.headers) : '');
            const chartKey = viz.description + "-chart" + (viz.chart_html ? viz.chart_html.substring(0, 50) : '');

            // Check if we have a table to render
            if (viz.table_data && !renderedRef.current.visualizations[tableKey]) {
              console.log(`[PARTIAL_RESULT] Rendering table for "${viz.description}"`);

              // Create a container for this visualization if it doesn't exist using safe selector
              let vizContainer = safeQuerySelector(
                visualizationsContainer,
                viz.description,
                vizIndex
              );

              if (!vizContainer) {
                console.log(`[PARTIAL_RESULT] Creating new visualization container for "${viz.description}"`);
                vizContainer = document.createElement('div');
                vizContainer.className = 'visualization-item';

                // Set safe identifiers including fallback mechanisms
                setSafeElementIdentifiers(vizContainer, viz.description, vizIndex);

                // Add title
                const titleElement = document.createElement('h3');
                titleElement.textContent = viz.description;
                vizContainer.appendChild(titleElement);

                visualizationsContainer.appendChild(vizContainer);
              }

              // Create a container for the table
              const tableContainer = document.createElement('div');
              tableContainer.className = 'table-container';

              // Render just the table
              const tableViz = {
                description: viz.description,
                table_data: viz.table_data
              };

              console.log(`[PARTIAL_RESULT] Calling renderVisualizations for table of "${viz.description}"`);
              renderVisualizations([tableViz], tableContainer);

              // Add the table to the visualization container
              vizContainer.appendChild(tableContainer);

              // Mark this table as rendered
              renderedRef.current.visualizations[tableKey] = true;
              console.log(`[PARTIAL_RESULT] Table for "${viz.description}" rendered successfully`);
            } else if (viz.table_data) {
              console.log(`[PARTIAL_RESULT] Table for "${viz.description}" already rendered, skipping`);
            }

            // Check if we have a chart to render and it's not already rendered
            if (viz.chart_html) {
              console.log(`[PARTIAL_RESULT] Processing chart for "${viz.description}"`);
              console.log(`[PARTIAL_RESULT] Chart HTML includes </script>:`, viz.chart_html.includes('</script>'));
              console.log(`[PARTIAL_RESULT] Chart already rendered:`, !!renderedRef.current.visualizations[chartKey]);

              if (viz.chart_html.includes('</script>') && !renderedRef.current.visualizations[chartKey]) {
                console.log(`[PARTIAL_RESULT] Rendering chart for "${viz.description}"`);

                // Find the visualization container using safe selector
                const vizContainer = safeQuerySelector(
                  visualizationsContainer,
                  viz.description,
                  vizIndex
                );

                if (vizContainer) {
                  // Create a container for the chart
                  const chartContainer = document.createElement('div');
                  chartContainer.className = 'chart-container';

                  // Render just the chart
                  const chartViz = {
                    description: viz.description,
                    chart_html: viz.chart_html
                  };

                  console.log(`[PARTIAL_RESULT] Calling renderVisualizations for chart of "${viz.description}"`);
                  renderVisualizations([chartViz], chartContainer);

                  // Add the chart to the visualization container
                  vizContainer.appendChild(chartContainer);

                  // Defer chart script execution to next frame to ensure canvas is in DOM
                  console.log(`[PARTIAL_RESULT] Deferring chart scripts for "${viz.description}"`);
                  const deferredContainer = chartContainer;
                  requestAnimationFrame(() => executeChartScripts(deferredContainer));

                  // Mark this chart as rendered
                  renderedRef.current.visualizations[chartKey] = true;
                  console.log(`[PARTIAL_RESULT] Chart for "${viz.description}" rendered successfully`);
                } else {
                  console.warn(`[PARTIAL_RESULT] Visualization container not found for: ${viz.description}`);
                  const errorElement = createErrorFallbackElement(
                    viz.description,
                    'Chart container not found'
                  );
                  visualizationsContainer.appendChild(errorElement);
                }
              } else if (!viz.chart_html.includes('</script>')) {
                console.log(`[PARTIAL_RESULT] Chart HTML for "${viz.description}" is incomplete, waiting for complete script`);
              } else {
                console.log(`[PARTIAL_RESULT] Chart for "${viz.description}" already rendered, skipping`);
              }
            }
          });
        } else {
          console.log('[PARTIAL_RESULT] No visualizations to render');
        }
      }
    }
  }, [result, parsedPartialText, contentRef]);

  return (
    <div className="partial-result">
      <div className="partial-content" ref={contentRef}>
        {/* Content will be rendered incrementally */}
      </div>
      <div className="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  );
};

export default PartialResultRenderer;
